import { create } from 'zustand';
import {
  directionForLocale,
  isLocale,
  setLocale as setI18nLocale,
  type Locale,
} from '@budgeting101/i18n';

const STORAGE_KEY = 'ledger-language';

/**
 * English is the product default. Only an explicit choice — persisted by
 * setLocale from the language selector — switches away from it.
 *
 * The browser's Accept-Language used to decide this, which meant anyone whose
 * device was set to Arabic landed in an Arabic, right-to-left app they had not
 * asked for, including when following a deep link in from another product.
 */
function storedLocale(): Locale {
  const stored = localStorage.getItem(STORAGE_KEY);
  return isLocale(stored) ? stored : 'en';
}

function applyLocale(locale: Locale) {
  setI18nLocale(locale);
  document.documentElement.lang = locale;
  document.documentElement.dir = directionForLocale(locale);
}

interface LanguageState {
  locale: Locale;
  setLocale: (locale: Locale) => void;
}

const initialLocale = storedLocale();
setI18nLocale(initialLocale);

export const useLanguageStore = create<LanguageState>((set) => ({
  locale: initialLocale,
  setLocale(locale) {
    localStorage.setItem(STORAGE_KEY, locale);
    applyLocale(locale);
    set({ locale });
  },
}));

/** Apply language metadata before React paints to avoid an LTR flash in Arabic. */
export function initializeLanguage() {
  applyLocale(useLanguageStore.getState().locale);
}
